import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPlanners } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Plus, BarChart3, List, Wrench, Users } from 'lucide-react';

export default function ToolkitPage() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [planners, setPlanners] = useState([]);
  const canCreate = hasRole(['admin', 'project_manager']);

  useEffect(() => {
    getPlanners().then(res => setPlanners(res.data.data || [])).catch(() => {});
  }, []);

  const marketingCount = planners.filter(p => (p.kind || 'marketing_commerciale') === 'marketing_commerciale').length;
  const recruitmentCount = planners.filter(p => p.kind === 'recruitment_sales').length;

  return (
    <div className="space-y-6" data-testid="toolkit-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Toolkit</h1>
        <p className="text-gray-500">Strumenti operativi per il team</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Tool Card: Planner Previsionale */}
        <Card className="border border-orange-200 hover:border-orange-300 hover:shadow-md transition-all cursor-pointer group" data-testid="tool-card-planner">
          <CardContent className="p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center shrink-0 group-hover:bg-orange-200 transition-colors">
                <BarChart3 className="w-5 h-5 text-orange-600" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 text-sm leading-tight">Planner Previsionale Marketing & Commerciale</h3>
                <p className="text-xs text-gray-500 mt-1">Costruisci previsionali completi partendo da un obiettivo di fatturato</p>
              </div>
            </div>
            {marketingCount > 0 && (
              <p className="text-xs text-gray-400 mb-3">{marketingCount} planner salvati</p>
            )}
            <div className="flex gap-2">
              {canCreate && (
                <Button size="sm" onClick={() => navigate('/toolkit/planner/new')} data-testid="new-planner-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Nuovo
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => navigate('/toolkit/planners?kind=marketing_commerciale')} data-testid="view-planners-btn">
                <List className="w-3.5 h-3.5 mr-1" /> Vedi tutti
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tool Card: Planner Reclutamento & Vendita */}
        <Card className="border border-indigo-200 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer group" data-testid="tool-card-recruitment-planner">
          <CardContent className="p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0 group-hover:bg-indigo-200 transition-colors">
                <Users className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 text-sm leading-tight">Planner Reclutamento & Vendita</h3>
                <p className="text-xs text-gray-500 mt-1">Calcola quanti venditori (full-cycle o BDR+Closer) servono per raggiungere il fatturato target</p>
              </div>
            </div>
            {recruitmentCount > 0 && (
              <p className="text-xs text-gray-400 mb-3">{recruitmentCount} planner salvati</p>
            )}
            <div className="flex gap-2">
              {canCreate && (
                <Button size="sm" onClick={() => navigate('/toolkit/recruitment-planner/new')} data-testid="new-recruitment-planner-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Nuovo
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => navigate('/toolkit/planners?kind=recruitment_sales')} data-testid="view-recruitment-planners-btn">
                <List className="w-3.5 h-3.5 mr-1" /> Vedi tutti
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Placeholder per futuri tool */}
        <Card className="border border-dashed border-gray-200 bg-gray-50/50">
          <CardContent className="p-5 flex flex-col items-center justify-center text-center h-full min-h-[140px]">
            <Wrench className="w-8 h-8 text-gray-300 mb-2" />
            <p className="text-sm text-gray-400">Prossimamente...</p>
            <p className="text-xs text-gray-300">Nuovi strumenti in arrivo</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
